import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

import { computed, onBeforeMount, Ref, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useGettext } from '@jshmrtn/vue3-gettext';
import { Flash } from '@/core/Flash';
import { ErrorBag, handleFormError, handleLoadError, TabId } from '@/core/common';
import { getAllLanguages, Language } from '@/modules/parameters/Language';
import PageLayout from '@/core/components/PageLayout.vue';
import AppTabs from '@/core/components/AppTabs.vue';
import AppButton from '@/core/components/form/AppButton.vue';
import AppFormGroup from '@/core/components/form/AppFormGroup.vue';
import AppInput from '@/core/components/form/AppInput.vue';
import AppEditor from '@/core/components/editor/AppEditor.vue';
import { Customer, useCustomer } from '@/modules/customer/Customer';
import { CustomerCmsPage, CustomerCmsPageResource, Slot, useCustomerCmsPage } from '@/modules/customer/CustomerCmsPage';


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerCmsPageIndex',
  setup(__props) {

const { $gettext, interpolate } = useGettext();
const route = useRoute();
const tabs = ref<TabId[]>([
	{ id: Slot.PublicTicketForm, name: $gettext('Rapportformulär') },
]);
const currentTab = ref<string>(Slot.PublicTicketForm);
const resource = ref<CustomerCmsPageResource>();
const customer = ref<Customer>();
const pageTitle = computed(() => interpolate(
	$gettext('CMS-sidor för %{ name }'),
	{name: customer.value?.name || ''},
));
const cmsPages = ref<Record<Slot, Record<string, CustomerCmsPage>>>({
	[Slot.PublicTicketForm]: {},
});
const errors = ref<Record<Slot, Record<string, Ref<ErrorBag>>>>({
	[Slot.PublicTicketForm]: {},
});
const languages = ref<Language[]>([]);

const load = async () => {
	if (!route.params.customer_id) {
		return;
	}

	try {
		customer.value = await useCustomer().get(route.params.customer_id as string);

		resource.value = useCustomerCmsPage(customer.value?.id);
		const pages = await resource.value.getAll();

		pages.forEach(page => {
			page.exists = true;
			cmsPages.value[page.slot][page.language] = page;
		});

	} catch (e) {
		handleLoadError(e as Error, 'customer.index');
	}

	languages.value = await getAllLanguages();

	for (const slotName in cmsPages.value) {
		languages.value.forEach(language => {
			if (!cmsPages.value[slotName][language.id]) {
				cmsPages.value[slotName][language.id] = {
					exists: false,
					slot: Slot.PublicTicketForm,
					language: language.id,
					headline: '',
					text: '',
					created_time: '',
				};
			}

			errors.value[slotName][language.id] = {
				value: {},
			};
		});
	}
};

watch(route, load);
onBeforeMount(load);

const save = async () => {
	if (!resource.value) {
		return;
	}

	const requests: Promise<CustomerCmsPage | void>[] = [];

	for (const slotName in cmsPages.value) {
		for (const languageId in cmsPages.value[slotName]) {
			errors.value[slotName][languageId].value = {};

			const page = cmsPages.value[slotName][languageId];
			let promise;

			if (page.headline === '' && page.text === '') {
				if (!page.exists) {
					continue;
				}

				promise = resource.value.delete(languageId, slotName)
					.then(() => {
						page.exists = false;
					})
				;

			} else {
				promise = resource.value.write(page)
					.then(() => {
						page.exists = true;
					})
				;
			}

			promise = promise
				.catch(e => {
					handleFormError(e as Error, errors.value[slotName][languageId]);
				})
			;

			requests.push(promise);
		}
	}

	await Promise.all(requests);
	Flash.success($gettext('CMS-sidorna har sparats.'));
};

return (_ctx: any,_cache: any) => {
  const _component_translate = _resolveComponent("translate")!

  return (_openBlock(), _createBlock(PageLayout, {
    "page-title": pageTitle.value,
    "base-tag": "form",
    onSubmit: _withModifiers(save, ["prevent"])
  }, {
    "header-actions": _withCtx(() => [
      _createVNode(AppButton, {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('cancel')))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_translate, null, {
            default: _withCtx(() => [
              _createTextVNode("Avbryt")
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(AppButton, { type: "submit" }, {
        default: _withCtx(() => [
          _createVNode(_component_translate, null, {
            default: _withCtx(() => [
              _createTextVNode("Spara")
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(AppTabs, {
        tabs: tabs.value,
        current: currentTab.value,
        onOpen: _cache[1] || (_cache[1] = ($event: any) => (currentTab.value = $event))
      }, null, 8, ["tabs", "current"]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
        return _withDirectives((_openBlock(), _createElementBlock("div", {
          key: tab.id
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(languages.value, (language) => {
            return (_openBlock(), _createElementBlock("div", {
              key: language.id,
              class: "panel"
            }, [
              _createElementVNode("h2", null, _toDisplayString(language.name), 1),
              _createVNode(AppFormGroup, {
                id: `cms_page_headline_${language.id}_${tab.id}`,
                errors: errors.value[tab.id][language.id].value.headline,
                label: _unref($gettext)('Rubrik')
              }, {
                default: _withCtx(() => [
                  _createVNode(AppInput, {
                    id: `cms_page_headline_${language.id}_${tab.id}`,
                    modelValue: cmsPages.value[tab.id][language.id].headline,
                    "onUpdate:modelValue": ($event: any) => ((cmsPages.value[tab.id][language.id].headline) = $event),
                    modelModifiers: { trim: true }
                  }, null, 8, ["id", "modelValue", "onUpdate:modelValue"])
                ]),
                _: 2
              }, 1032, ["id", "errors", "label"]),
              _createVNode(AppEditor, {
                modelValue: cmsPages.value[tab.id][language.id].text,
                "onUpdate:modelValue": ($event: any) => ((cmsPages.value[tab.id][language.id].text) = $event),
                errors: errors.value[tab.id][language.id].value.text,
                label: _unref($gettext)('Text')
              }, null, 8, ["modelValue", "onUpdate:modelValue", "errors", "label"])
            ]))
          }), 128))
        ], 512)), [
          [_vShow, currentTab.value === tab.id]
        ])
      }), 128))
    ]),
    _: 1
  }, 8, ["page-title", "onSubmit"]))
}
}

})